import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import { BRANDS, EXPLORER_ITEMS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Explorador() {
  const { t, lp } = useLanguage();
  const [open, setOpen] = useState(null);
  const items = EXPLORER_ITEMS;

  const active = open ? EXPLORER_ITEMS.find((i) => i.id === open) : null;
  const activeBrand = active ? BRANDS.find((b) => b.slug === active.brand) : null;

  return (
    <div data-testid="explorer-page" className="pt-20">
      <Seo title={t("seo.explorer.title")} description={t("seo.explorer.desc")} />

      <section className="pt-16 lg:pt-20 pb-20 max-w-7xl mx-auto px-6">
        <Reveal className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:items-end">
          <div className="lg:col-span-7">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("explorer.tag")}</span>
            <h1 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4">{t("explorer.title")}</h1>
          </div>
          <p className="lg:col-span-5 text-base text-[#A8A6A0] font-light leading-relaxed">{t("home.explorer_text")}</p>
        </Reveal>

        <div className="border-t border-white/10 mb-10" />

        {items.length === 0 ? (
          <p className="text-sm text-[#A8A6A0] font-light py-20" data-testid="explorer-empty">{t("explorer.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it, i) => (
              <Reveal key={it.id} delay={(i % 3) * 0.08}>
                <button
                  type="button"
                  onClick={() => setOpen(it.id)}
                  data-testid={`explorer-item-${it.id}`}
                  className="img-frame w-full text-left aspect-[4/5] border border-white/10 group hover:border-[#C2A675]/50 transition-colors"
                >
                  <Img id={it.image} alt={BRANDS.find((b) => b.slug === it.brand)?.name} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent p-6 flex flex-col justify-end">
                    <span className="mono-tag text-[10px] text-[#C2A675] uppercase">{BRANDS.find((b) => b.slug === it.brand)?.name}</span>
                    <h2 className="serif-display text-white text-xl font-light mt-2 leading-snug">{t(`explorer_items.${it.id}.name`)}</h2>
                    <span className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#A8A6A0] group-hover:text-[#C2A675] transition-colors inline-flex items-center gap-2">
                      {t("common.explore")} <ArrowRight size={12} aria-hidden="true" className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {active && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={t(`explorer_items.${active.id}.name`)} data-testid="explorer-modal">
          <button type="button" aria-label={t("common.close")} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(null)} data-testid="explorer-modal-backdrop" />
          <div className="relative bg-[#121316] border border-white/10 w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto grid grid-cols-1 sm:grid-cols-2">
            <div className="relative aspect-square sm:aspect-auto sm:min-h-[420px]">
              <Img id={active.image} alt={activeBrand?.name} label={t("common.pending_image")} priority className="absolute inset-0 w-full h-full object-cover img-tone" />
            </div>
            <div className="p-8 sm:p-10 flex flex-col">
              <button type="button" onClick={() => setOpen(null)} aria-label={t("common.close")} data-testid="explorer-modal-close" className="self-end text-[#A8A6A0] hover:text-white transition-colors -mt-2 -mr-2 mb-4">
                <X size={20} aria-hidden="true" />
              </button>
              <span className="mono-tag text-[10px] uppercase tracking-[0.2em] text-[#C2A675]">{t("explorer.detail_brand")}: {activeBrand?.name}</span>
              <h2 className="serif-display text-3xl font-light text-white mt-3 mb-4 leading-tight">{t(`explorer_items.${active.id}.name`)}</h2>
              <p className="text-base text-[#A8A6A0] font-light leading-relaxed mb-6">{t(`explorer_items.${active.id}.desc`)}</p>
              <ul className="flex flex-wrap gap-2 mb-10">
                {active.applications.map((a) => (
                  <li key={a} className="px-3 py-1 border border-white/15 text-[10px] uppercase tracking-[0.14em] text-[#E8E6E1]">{t(`app_cats.${a}`)}</li>
                ))}
              </ul>
              <div className="mt-auto space-y-3">
                <Link
                  to={`${lp("/contactos")}?marca=${active.brand}&assunto=info`}
                  data-testid="explorer-detail-cta"
                  className="btn-solid w-full py-4"
                >
                  {t("explorer.detail_cta")} <ArrowRight size={14} aria-hidden="true" />
                </Link>
                <Link
                  to={lp(`/marcas/${active.brand}`)}
                  data-testid="explorer-detail-brand"
                  className="btn-ghost w-full py-4"
                >
                  {activeBrand?.name}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
